import React, { useMemo } from "react";
import { navigateToTop } from "../../utils/Constants.js";
import slug from "../../utils/slug.js";
import "../../styles/components/interview-tabs/_question-bank.scss";

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconDone = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="8" cy="8" r="7" fill="currentColor" opacity="0.12" />
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
        <path
            d="M5 8l2.2 2.2L11 6"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconEmpty = () => (
    <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
    >
        <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.4" />
    </svg>
);

const IconArrow = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M3 7h8M8 4l3 3-3 3"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

// ─── Component ────────────────────────────────────────────────────────────────
export default function QuestionBank({
    showQuestions,
    groupedQuestions,
    handleQuestionClick,
    questionRef,
    recordings = [],
}) {
    // Build a count map keyed by every possible identifier
    const recordingsCountByKey = useMemo(() => {
        const map = new Map();
        for (const rec of recordings) {
            const add = (key) => {
                if (!key) return;
                const k = String(key);
                map.set(k, (map.get(k) || 0) + 1);
            };
            add(rec.question_slug);
            add(rec.questionSlug);
            add(rec.question_id);
            add(rec.questionId);
            if (rec.question) add(slug(rec.question));
            if (rec.questionText) add(slug(rec.questionText));
            if (rec.question_title) add(slug(rec.question_title));
        }
        return map;
    }, [recordings]);

    const onRowActivate = (q) => {
        handleQuestionClick(q);
        navigateToTop();
    };

    // Flatten into category groups, preserving order
    const groups = useMemo(
        () =>
            Object.entries(groupedQuestions).map(([category, qs]) => ({
                category,
                qs,
            })),
        [groupedQuestions]
    );

    if (!showQuestions || groups.length === 0) return null;

    return (
        <div className="qbank" ref={questionRef}>
            {groups.map(({ category, qs }, gi) => {
                // Count how many in this group are recorded
                const doneCount = qs.filter((q) => {
                    const qSlug = slug(q.slug ?? q.id ?? q.question);
                    const qIdKey = q.id != null ? String(q.id) : null;
                    return (
                        (qIdKey && recordingsCountByKey.get(qIdKey)) ||
                        recordingsCountByKey.get(qSlug)
                    );
                }).length;

                return (
                    <div
                        className="qbank__group"
                        key={category}
                        style={{ "--group-index": gi }}
                    >
                        {/* Group header */}
                        <div className="qbank__group-header">
                            <h2 className="qbank__group-title">{category}</h2>
                            <div className="qbank__group-meta">
                                <span className="qbank__group-progress">
                                    <span
                                        className="qbank__group-bar-fill"
                                        style={{
                                            "--done-pct": `${qs.length ? Math.round((doneCount / qs.length) * 100) : 0}%`,
                                        }}
                                        aria-hidden="true"
                                    />
                                </span>
                                <span className="qbank__group-count">
                                    {doneCount}/{qs.length}
                                </span>
                            </div>
                        </div>

                        {/* Question rows */}
                        <ul className="qbank__list" role="list">
                            {qs.map((q, qi) => {
                                const key = q._key ?? q.id ?? q.question;
                                if (!key) return null;

                                const qSlug = slug(
                                    q.slug ?? q.id ?? q.question
                                );
                                const qIdKey =
                                    q.id != null ? String(q.id) : null;
                                const count =
                                    (qIdKey &&
                                        recordingsCountByKey.get(qIdKey)) ||
                                    recordingsCountByKey.get(qSlug) ||
                                    0;
                                const done = count > 0;

                                return (
                                    <li key={key} style={{ "--row-index": qi }}>
                                        <button
                                            type="button"
                                            className={`qbank__row${done ? " is-done" : ""}`}
                                            onClick={() => onRowActivate(q)}
                                            aria-label={`${q.question}${done ? ` — ${count} attempt${count === 1 ? "" : "s"}` : " — not attempted"}`}
                                        >
                                            {/* Status icon */}
                                            <span
                                                className={`qbank__status${done ? " qbank__status--done" : " qbank__status--empty"}`}
                                                aria-hidden="true"
                                            >
                                                {done ? (
                                                    <IconDone />
                                                ) : (
                                                    <IconEmpty />
                                                )}
                                            </span>

                                            {/* Main content */}
                                            <span className="qbank__content">
                                                <span className="qbank__question">
                                                    {q.question}
                                                </span>
                                                {done && count > 1 && (
                                                    <span className="qbank__attempts">
                                                        {count} attempts
                                                    </span>
                                                )}
                                            </span>

                                            {/* Arrow */}
                                            <span
                                                className="qbank__arrow"
                                                aria-hidden="true"
                                            >
                                                <IconArrow />
                                            </span>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                );
            })}
        </div>
    );
}
